import L from 'leaflet';
import Supercluster from 'supercluster';
import type { HdProjection } from './dynmapCrs';
import type { PhotoPoint } from './types';
import { getSiteBase } from './config';

/** Square photo tile size (Google/Apple-style map photos). */
const TILE = 56;
const CLUSTER = 64;

export interface PhotoLayerHandle {
  setWorld(world: string): void;
  destroy(): void;
  getCount(): number;
}

export function createPhotoLayer(
  map: L.Map,
  proj: HdProjection,
  points: PhotoPoint[],
  activeWorld: string,
): PhotoLayerHandle {
  let world = activeWorld;
  const cluster = new Supercluster<{ point: PhotoPoint }>({
    radius: 64,
    maxZoom: 16,
    minPoints: 2,
  });

  const group = L.layerGroup().addTo(map);
  let markers: L.Layer[] = [];

  function filtered(): PhotoPoint[] {
    return points.filter((p) => p.world === world);
  }

  function rebuildIndex(): void {
    const feats = filtered().map((p) => {
      const ll = proj.locationToLatLng(p.x, p.y, p.z);
      return {
        type: 'Feature' as const,
        properties: { point: p },
        geometry: {
          type: 'Point' as const,
          coordinates: [ll.lng, ll.lat],
        },
      };
    });
    cluster.load(feats);
  }

  function clearMarkers(): void {
    markers.forEach((m) => group.removeLayer(m));
    markers = [];
  }

  function render(): void {
    clearMarkers();
    const b = map.getBounds();
    const z = Math.round(map.getZoom());
    const bbox: [number, number, number, number] = [
      b.getWest(),
      b.getSouth(),
      b.getEast(),
      b.getNorth(),
    ];
    const clusters = cluster.getClusters(bbox, z);

    for (const f of clusters) {
      const [lng, lat] = f.geometry.coordinates;
      const props = f.properties as {
        cluster?: boolean;
        point_count?: number;
        point?: PhotoPoint;
        cluster_id?: number;
      };

      if (props.cluster) {
        const count = props.point_count || 0;
        const clusterId = props.cluster_id!;
        // Peek leaves for stack preview thumbs (up to 3)
        const leaves = cluster.getLeaves(clusterId, 3, 0) as Array<{
          properties: { point: PhotoPoint };
        }>;
        const thumbs = leaves
          .map((leaf) => leaf.properties?.point)
          .filter(Boolean) as PhotoPoint[];

        const stackLayers = [2, 1, 0]
          .map((i) => {
            const p = thumbs[i];
            if (!p) {
              return `<span class="photo-stack-card photo-stack-card--empty photo-stack-card--${i}"></span>`;
            }
            return `<span class="photo-stack-card photo-stack-card--${i}"><img src="${escapeAttr(resolveImage(p.image))}" alt="" loading="lazy" decoding="async" /></span>`;
          })
          .join('');

        const label = count > 99 ? '99+' : String(count);
        const html = `<div class="photo-cluster" role="button" tabindex="0" title="${count} фото">
          <div class="photo-stack">${stackLayers}</div>
          <span class="photo-cluster-badge">${label}</span>
        </div>`;

        const icon = L.divIcon({
          className: 'photo-cluster-wrap',
          html,
          iconSize: [CLUSTER, CLUSTER],
          iconAnchor: [CLUSTER / 2, CLUSTER / 2],
        });
        const m = L.marker(L.latLng(lat, lng), { icon, zIndexOffset: 200 + count });
        m.on('click', () => {
          const expansionZoom = Math.min(
            cluster.getClusterExpansionZoom(clusterId),
            map.getMaxZoom(),
          );
          map.setView(L.latLng(lat, lng), expansionZoom, { animate: true });
        });
        m.addTo(group);
        markers.push(m);
      } else if (props.point) {
        const p = props.point;
        const title = escapeAttr(p.title || p.player || 'Фото');
        const html = `<div class="photo-marker" title="${title}">
          <img src="${escapeAttr(resolveImage(p.image))}" alt="" loading="lazy" decoding="async" />
          <span class="photo-marker-ring"></span>
        </div>`;
        const icon = L.divIcon({
          className: 'photo-marker-wrap',
          html,
          iconSize: [TILE, TILE],
          iconAnchor: [TILE / 2, TILE / 2],
        });
        const m = L.marker(L.latLng(lat, lng), { icon, zIndexOffset: 100 });
        const site = getSiteBase();
        const shotUrl = `${site}/screenshots.html?shot=${encodeURIComponent(String(p.id))}`;
        m.bindPopup(
          `<div class="photo-popup">
            <a class="photo-popup-shot" href="${escapeAttr(shotUrl)}" target="_blank" rel="noreferrer">
              <img src="${escapeAttr(resolveImage(p.image))}" alt="" />
            </a>
            <div class="photo-popup-meta">
              <strong>${escapeHtml(p.player || 'Игрок')}</strong>
              <span class="photo-popup-title">${escapeHtml(p.title || 'Скриншот')}</span>
              <span class="photo-popup-coords">X:${Math.round(p.x)} Y:${Math.round(p.y)} Z:${Math.round(p.z)}</span>
              <a class="photo-popup-link" href="${escapeAttr(shotUrl)}" target="_blank" rel="noreferrer">Открыть в галерее</a>
            </div>
          </div>`,
          { maxWidth: 280, className: 'photo-popup-wrap', offset: [0, -8] },
        );
        m.addTo(group);
        markers.push(m);
      }
    }
  }

  rebuildIndex();
  render();
  map.on('moveend zoomend', render);

  return {
    setWorld(next: string) {
      world = next;
      rebuildIndex();
      render();
    },
    destroy() {
      map.off('moveend zoomend', render);
      clearMarkers();
      map.removeLayer(group);
    },
    getCount() {
      return filtered().length;
    },
  };
}

function resolveImage(path: string): string {
  if (!path) return '';
  if (/^(https?:)?\/\//i.test(path) || path.startsWith('data:')) return path;
  if (path.startsWith('/api/')) return path;
  if (path.startsWith('/registry/')) {
    return `/api${path}`;
  }
  return path;
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/'/g, '&#39;');
}
